import React, { useState, useEffect, useCallback } from 'react';
import { createLeaveRequest, getLeaveRequests, deleteLeaveRequest, LeaveRequestData } from '../../services/leaveService';
import { format, differenceInCalendarDays, parseISO } from 'date-fns';
import { DatePicker } from '../ui/DatePicker';

const leaveTypes = [
    { value: 'ANNUAL', label: 'Annual Leave', icon: '🏖️', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    { value: 'SICK', label: 'Sick Leave', icon: '🏥', color: 'bg-red-50 text-red-700 border-red-200' },
    { value: 'PERSONAL', label: 'Personal Leave', icon: '👤', color: 'bg-purple-50 text-purple-700 border-purple-200' },
    { value: 'UNPAID', label: 'Unpaid Leave', icon: '💰', color: 'bg-amber-50 text-amber-700 border-amber-200' },
];

const statusStyles: Record<string, string> = {
    PENDING: 'bg-amber-50 text-amber-700 border border-amber-200',
    APPROVED: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    REJECTED: 'bg-red-50 text-red-700 border border-red-200',
};

const LeaveRequestForm: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'request' | 'history'>('request');

    // Request form state
    const [formData, setFormData] = useState({ startDate: '', endDate: '', type: 'ANNUAL', reason: '' });
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    // History state
    const [leaves, setLeaves] = useState<LeaveRequestData[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const showToast = (type: 'success' | 'error', message: string) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 4000);
    };

    const loadHistory = useCallback(async () => {
        setHistoryLoading(true);
        try {
            const data = await getLeaveRequests();
            setLeaves(Array.isArray(data) ? data : data?.data || []);
        } catch {
            console.error('Failed to load leave history');
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'history') loadHistory();
    }, [activeTab, loadHistory]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.startDate || !formData.endDate) {
            showToast('error', 'Please select both start and end dates');
            return;
        }
        if (new Date(formData.startDate) > new Date(formData.endDate)) {
            showToast('error', 'End date must be after start date');
            return;
        }

        setSubmitting(true);
        try {
            await createLeaveRequest(formData);
            showToast('success', 'Leave request submitted successfully!');
            setFormData({ startDate: '', endDate: '', type: 'ANNUAL', reason: '' });
            // Auto-switch to history after short delay
            setTimeout(() => setActiveTab('history'), 1500);
        } catch (err: any) {
            showToast('error', err.response?.data?.message || err.message || 'Failed to submit request');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = async (id: string) => {
        try {
            await deleteLeaveRequest(id);
            showToast('success', 'Leave request cancelled');
            loadHistory();
        } catch {
            showToast('error', 'Failed to cancel request');
        }
    };

    const dayCount = formData.startDate && formData.endDate
        ? differenceInCalendarDays(parseISO(formData.endDate), parseISO(formData.startDate)) + 1
        : 0;

    const selectedType = leaveTypes.find(t => t.value === formData.type);

    return (
        <div className="max-w-3xl mx-auto">
            {/* Toast notification */}
            {toast && (
                <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 text-sm font-medium transition-all ${
                    toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
                }`} style={{ animation: 'pageFadeIn 0.2s ease-out' }}>
                    {toast.type === 'success' ? '✓' : '✕'} {toast.message}
                </div>
            )}

            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-slate-800">Leave Management</h1>
                <p className="text-slate-500 text-sm mt-1">Submit leave requests and track their status</p>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl mb-6 w-fit">
                <button
                    onClick={() => setActiveTab('request')}
                    className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeTab === 'request'
                            ? 'bg-white text-slate-800 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                    📝 New Request
                </button>
                <button
                    onClick={() => setActiveTab('history')}
                    className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeTab === 'history'
                            ? 'bg-white text-slate-800 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                    📋 My History
                </button>
            </div>

            {/* Request Tab */}
            {activeTab === 'request' && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" style={{ animation: 'pageFadeIn 0.2s ease-out' }}>
                    <form onSubmit={handleSubmit}>
                        {/* Leave Type Selection */}
                        <div className="p-6 border-b border-slate-100">
                            <label className="block text-sm font-semibold text-slate-700 mb-3">Leave Type</label>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {leaveTypes.map(lt => (
                                    <button
                                        key={lt.value}
                                        type="button"
                                        onClick={() => setFormData({ ...formData, type: lt.value })}
                                        className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                                            formData.type === lt.value
                                                ? `${lt.color} border-current ring-2 ring-offset-1 ring-current/20`
                                                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                                        }`}
                                    >
                                        <span className="text-lg">{lt.icon}</span>
                                        <span>{lt.label.replace(' Leave', '')}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Date Selection */}
                        <div className="p-6 border-b border-slate-100">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <DatePicker
                                    label="Start Date"
                                    value={formData.startDate}
                                    onChange={(v) => setFormData({ ...formData, startDate: v })}
                                    placeholder="Pick start date"
                                />
                                <DatePicker
                                    label="End Date"
                                    value={formData.endDate}
                                    onChange={(v) => setFormData({ ...formData, endDate: v })}
                                    min={formData.startDate || undefined}
                                    placeholder="Pick end date"
                                />
                            </div>

                            {/* Days count badge */}
                            {dayCount > 0 && (
                                <div className="mt-3 flex items-center gap-2">
                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${selectedType?.color || 'bg-slate-100 text-slate-600'}`}>
                                        {selectedType?.icon} {dayCount} day{dayCount !== 1 ? 's' : ''} — {selectedType?.label}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Reason */}
                        <div className="p-6 border-b border-slate-100">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Reason <span className="text-slate-400 font-normal">(Optional)</span></label>
                            <textarea
                                value={formData.reason}
                                onChange={e => setFormData({ ...formData, reason: e.target.value })}
                                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-slate-50 focus:bg-white h-24 resize-none"
                                placeholder="Brief reason for your leave..."
                            />
                        </div>

                        {/* Submit */}
                        <div className="p-6 bg-slate-50">
                            <button
                                type="submit"
                                disabled={submitting || !formData.startDate || !formData.endDate}
                                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold py-3 px-6 rounded-xl transition-all shadow-sm hover:shadow-md disabled:shadow-none flex items-center justify-center gap-2"
                            >
                                {submitting ? (
                                    <>
                                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                        Submitting...
                                    </>
                                ) : (
                                    'Submit Leave Request'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
                <div style={{ animation: 'pageFadeIn 0.2s ease-out' }}>
                    {historyLoading ? (
                        <div className="space-y-3">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
                                    <div className="flex gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-slate-200" />
                                        <div className="flex-1 space-y-2">
                                            <div className="h-4 bg-slate-200 rounded w-1/3" />
                                            <div className="h-3 bg-slate-100 rounded w-1/2" />
                                        </div>
                                        <div className="w-20 h-6 bg-slate-200 rounded-full" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : leaves.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                            <div className="text-4xl mb-3">📋</div>
                            <h3 className="text-lg font-semibold text-slate-700">No leave requests yet</h3>
                            <p className="text-slate-500 text-sm mt-1">Submit your first leave request to see it here</p>
                            <button
                                onClick={() => setActiveTab('request')}
                                className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Create Request
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {leaves.map(leave => {
                                const lt = leaveTypes.find(t => t.value === leave.type);
                                const isExpanded = expandedId === leave.id;
                                const days = differenceInCalendarDays(parseISO(leave.endDate), parseISO(leave.startDate)) + 1;
                                return (
                                    <div
                                        key={leave.id}
                                        className="bg-white rounded-xl border border-slate-200 overflow-hidden transition-all hover:border-slate-300"
                                    >
                                        <div
                                            className="p-4 flex items-center gap-4 cursor-pointer"
                                            onClick={() => setExpandedId(isExpanded ? null : leave.id)}
                                        >
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg ${lt?.color || 'bg-slate-100'}`}>
                                                {lt?.icon || '📅'}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-slate-800">{lt?.label || leave.type}</span>
                                                    <span className="text-slate-400">·</span>
                                                    <span className="text-sm text-slate-500">{days} day{days !== 1 ? 's' : ''}</span>
                                                </div>
                                                <p className="text-sm text-slate-500 mt-0.5">
                                                    {format(parseISO(leave.startDate), 'MMM d, yyyy')} — {format(parseISO(leave.endDate), 'MMM d, yyyy')}
                                                </p>
                                            </div>
                                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusStyles[leave.status] || 'bg-slate-100 text-slate-600'}`}>
                                                {leave.status}
                                            </span>
                                            <svg className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>

                                        {isExpanded && (
                                            <div className="px-4 pb-4 pt-0 border-t border-slate-100">
                                                <div className="mt-3 space-y-2 text-sm">
                                                    {leave.reason && (
                                                        <div>
                                                            <span className="font-medium text-slate-600">Reason:</span>
                                                            <span className="ml-2 text-slate-500">{leave.reason}</span>
                                                        </div>
                                                    )}
                                                    {leave.adminNotes && (
                                                        <div className="bg-slate-50 rounded-lg p-3 mt-2">
                                                            <span className="font-medium text-slate-600">Admin Notes:</span>
                                                            <p className="text-slate-500 mt-1">{leave.adminNotes}</p>
                                                        </div>
                                                    )}
                                                    {leave.createdAt && (
                                                        <p className="text-slate-400 text-xs">
                                                            Submitted {format(parseISO(leave.createdAt), 'MMM d, yyyy \'at\' h:mm a')}
                                                        </p>
                                                    )}
                                                    {leave.status === 'PENDING' && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleCancel(leave.id); }}
                                                            className="mt-2 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                                                        >
                                                            Cancel Request
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LeaveRequestForm;
